/**
 * FmDateField - DS-branded date picker (wraps AntD DatePicker).
 *
 * SEALED API (muem9.28): route code passes data/semantic props only. There is no
 * raw AntD `size` escape hatch — control density is expressed with the semantic
 * `density` prop ('comfortable' | 'compact'), mapped to AntD sizing INSIDE the
 * wrapper. Mirrors the FmTextField seal (muem9.26).
 */
import React from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  FmFormField,
  type FmFormDensity,
  type FmDateFieldDescriptor,
  type FmFormFieldMetaProps,
} from './FmForm';
import { FmOverlayPortalContext } from './FmOverlayPortalContext';

/** Map semantic density → AntD control size. DS-internal; not exposed to routes. */
const ISO_DATE = 'YYYY-MM-DD';

const densityToControlSize = (density?: FmFormDensity): 'small' | 'middle' =>
  density === 'compact' ? 'small' : 'middle';

export interface FmDateFieldProps extends Omit<FmFormFieldMetaProps, 'size'> {
  descriptor?: FmDateFieldDescriptor;
  id?: string;
  value?: Dayjs | null;
  defaultValue?: Dayjs;
  /**
   * Fires with the committed date, or `null` when the field is cleared. Never
   * `undefined`, never a `null` date string, and never a stale re-submit of a
   * value the user already replaced: the wrapper normalises AntD's raw picker
   * callbacks before they reach route code.
   */
  onChange?: (date: Dayjs | null, dateString: string) => void;
  min?: Date | string;
  max?: Date | string;
  format?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Sealed accessible name for bare (unlabeled) date fields. Pass a localized string. */
  ariaLabel?: string;
}

const toDescriptor = (props: FmDateFieldProps): FmDateFieldDescriptor => ({
  kind: 'date',
  name: props.name ?? '',
  label: String(props.label ?? ''),
  helper: props.helper ? String(props.helper) : undefined,
  error: props.error ? String(props.error) : undefined,
  required: props.required,
  unit: props.unit ? String(props.unit) : undefined,
  basisNote: props.basisNote ? String(props.basisNote) : undefined,
  disabledReason: props.disabledReason ? String(props.disabledReason) : undefined,
  lockedReason: props.lockedReason ? String(props.lockedReason) : undefined,
  validation: props.validation,
  intent: props.intent,
  density: props.density,
  layoutPreset: props.layoutPreset,
  disabled: props.disabled,
  id: props.id,
  placeholder: props.placeholder,
  testId: props['data-testid'],
  value: props.value,
  defaultValue: props.defaultValue,
  onChange: props.onChange,
  min: props.min,
  max: props.max,
  format: props.format,
});

/**
 * The bare (unlabeled) picker branch. Split into its own component so every hook
 * below runs unconditionally — the labelled/descriptor branches return a
 * different element entirely and must not share this one's hook order.
 *
 * Three AntD / rc-picker (@rc-component/picker) behaviours shape the commit path:
 *
 * 1. When `format` is set the picker's input is a MASKED input: its React
 *    `onChange` is a deliberate no-op (`Selector/Input.js` → `onInternalChange`
 *    returns unless `!format`), and its text state only ever advances through
 *    per-keystroke mask edits or a `paste`. Text delivered any other way —
 *    Playwright `fill()` (a CDP `Input.insertText`), browser autofill, a
 *    password/date manager, dictation, an IME commit — is dropped on the floor
 *    and React restores the controlled DOM value. FmDateField therefore listens
 *    for the native `input` event ON THE REAL UNDERLYING `<input>` and commits a
 *    complete, in-format, in-bounds date itself.
 *
 * 2. Confirming (Enter, or closing the panel) submits the picker's INTERNAL
 *    calendar value, which only resyncs with the controlled `value` in a passive
 *    effect. A confirm that runs before that resync re-emits the value the field
 *    held BEFORE the typed date and, because it now differs from the controlled
 *    value, fires `onChange` with the stale date — or with `null` — clobbering
 *    the commit (timber-prices AC7: `date_from` snapped back to the default
 *    5-year window). While a typed commit is pending, a picker change carrying a
 *    different value than the committed text is that stale echo and is dropped.
 *    Every genuine later change — panel cell, clear button, further mask typing,
 *    preset — announces itself through `onCalendarChange` first, which releases
 *    the pending commit, so nothing real is swallowed.
 *
 * 3. AntD reports an empty change twice and inconsistently: clearing fires
 *    `onChange(null, null)` and then `onChange(undefined, null)`. An `undefined`
 *    date slips past a consumer's `=== null` check and a `null` date string
 *    breaks the promised `string`, so both arguments are normalised here.
 */
const FmBareDateField: React.FC<FmDateFieldProps> = (props) => {
  /**
   * The in-format date text this field committed from raw input text, kept until
   * the picker itself reports a different value.
   */
  const typedCommitRef = React.useRef<string | null>(null);
  // Inside a dialog the calendar opens in the dialog's own layer, never under it.
  const overlayPortalRef = React.useContext(FmOverlayPortalContext);
  const getPopupContainer = React.useCallback(
    () => overlayPortalRef?.current ?? document.body,
    [overlayPortalRef],
  );
  /** The real `<input>` AntD renders — the element typed text actually lands on. */
  const inputElRef = React.useRef<HTMLInputElement | null>(null);
  /** Latest-render commit handler, refreshed after every render. */
  const commitRef = React.useRef<(text: string) => void>(() => {});

  const nativeInputListener = React.useCallback((event: Event) => {
    const target = event.target as HTMLInputElement | null;
    if (target) commitRef.current(target.value);
  }, []);

  /**
   * Bind the typed-text capture to the picker's own `<input>`. React's delegated
   * listeners are not enough here: the picker owns that input, and the commit has
   * to see the text the instant it lands on the element, before the controlled
   * re-render reverts it.
   */
  const attachShell = React.useCallback(
    (node: HTMLSpanElement | null) => {
      const previous = inputElRef.current;
      if (previous) previous.removeEventListener('input', nativeInputListener);
      const next = node ? node.querySelector('input') : null;
      inputElRef.current = next;
      if (next) next.addEventListener('input', nativeInputListener);
    },
    [nativeInputListener],
  );

  const minDate = props.min ? dayjs(props.min) : undefined;
  const maxDate = props.max ? dayjs(props.max) : undefined;
  const disabledDate = (current: Dayjs) => {
    if (!current) return false;
    if (minDate && current.isBefore(minDate, 'day')) return true;
    if (maxDate && current.isAfter(maxDate, 'day')) return true;
    return false;
  };

  const format = props.format ?? ISO_DATE;

  const currentText = props.value ? props.value.format(format) : '';

  /**
   * Parse raw field text into a committable date: it must round-trip through the
   * field format, or through ISO `YYYY-MM-DD` (unambiguous in every locale, and
   * what a pasted or autofilled date usually is), so partial/garbage text such
   * as `2022-01` or `2022-1-01` never commits regardless of dayjs's lenient
   * fallback. It must also satisfy any min/max.
   */
  const parseCommittable = (raw: string | null | undefined): Dayjs | null => {
    const text = (raw ?? '').trim();
    if (!text) return null;
    const parsed = [format, ISO_DATE]
      .map((pattern) => ({ pattern, date: dayjs(text, pattern) }))
      .find(({ pattern, date }) => date.isValid() && date.format(pattern) === text)?.date;
    if (!parsed || disabledDate(parsed)) return null;
    return parsed;
  };

  /** The pending commit is held as the field shows it, whichever syntax was typed. */
  const commitTypedDate = (parsed: Dayjs) => {
    const text = parsed.format(format);
    typedCommitRef.current = text;
    props.onChange?.(parsed, text);
  };

  /**
   * Honour text delivered straight to the underlying `<input>` — the path the
   * masked picker input ignores. Committing here, rather than waiting for Enter,
   * also means the controlled `value` is already the typed date by the time the
   * picker confirms, so its own confirm re-submits the same date instead of
   * fighting the commit.
   */
  const commitInputText = (raw: string) => {
    const text = raw.trim();
    const parsed = parseCommittable(text);
    if (!parsed) {
      // Field text is being edited into something not (yet) committable — no
      // stale typed commit may survive it, so a real clear still reaches through.
      typedCommitRef.current = null;
      return;
    }
    if (parsed.format(format) === currentText) {
      typedCommitRef.current = currentText;
      return;
    }
    commitTypedDate(parsed);
  };

  React.useEffect(() => {
    commitRef.current = commitInputText;
  });

  /**
   * Commit typed input on Enter as well, for text that reached the field without
   * a native `input` event (jsdom `fireEvent.change`, a synthetic host input).
   * The remembered typed text wins over the DOM value, which a controlled
   * re-render may already have reverted to the committed date.
   */
  const commitTypedInput = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter') return;
    const target = event.target as HTMLInputElement | null;
    const text = (typedCommitRef.current ?? target?.value ?? '').trim();
    const parsed = parseCommittable(text);
    if (!parsed || parsed.format(format) === currentText) return;
    commitTypedDate(parsed);
  };

  /** Release the pending typed commit as soon as the picker moves elsewhere. */
  const releaseOnCalendarChange = (date: Dayjs | Dayjs[] | null) => {
    const pending = typedCommitRef.current;
    if (pending === null) return;
    const next = Array.isArray(date) ? date[0] : date;
    if ((next ? next.format(format) : '') !== pending) {
      typedCommitRef.current = null;
    }
  };

  /**
   * Normalise every picker submit before it leaves the wrapper.
   *
   * AntD reports an empty change twice and inconsistently — clearing fires
   * `onChange(null, null)` and then `onChange(undefined, null)` — so a consumer
   * typed against `Dayjs | null` / `string` would see `undefined` slip past a
   * `=== null` check and a `null` where a string was promised.
   */
  const handlePickerChange = (
    date: Dayjs | null | undefined,
    dateString: string | string[] | null,
  ) => {
    const pending = typedCommitRef.current;
    const nextDate = date ?? null;
    const nextText = nextDate ? nextDate.format(format) : '';
    if (pending !== null && nextText !== pending) {
      // Stale confirm echo (a superseded date, or an empty submit) of the value
      // the typed commit already replaced.
      return;
    }
    const reported = Array.isArray(dateString) ? dateString[0] : dateString;
    typedCommitRef.current = null;
    props.onChange?.(nextDate, reported ?? nextText);
  };

  return (
    <span className="fm-date-field-shell" ref={attachShell}>
      <DatePicker
        id={props.id}
        value={props.value}
        defaultValue={props.defaultValue}
        onChange={handlePickerChange}
        onCalendarChange={releaseOnCalendarChange}
        onKeyDown={commitTypedInput}
        format={format}
        placeholder={props.placeholder ?? 'Select date'}
        disabled={props.disabled}
        disabledDate={disabledDate}
        size={densityToControlSize(props.density)}
        aria-label={props.ariaLabel}
        className="fm-date-field"
        data-testid={props['data-testid']}
        getPopupContainer={getPopupContainer}
        classNames={{ popup: { root: 'fm-date-field__popup' } }}
      />
    </span>
  );
};

export const FmDateField: React.FC<FmDateFieldProps> = (props) => {
  if (props.descriptor) {
    return <FmFormField descriptor={props.descriptor} />;
  }

  if (props.name && props.label) {
    return <FmFormField descriptor={toDescriptor(props)} />;
  }

  return <FmBareDateField {...props} />;
};

export default FmDateField;
